import { useState, useEffect } from "react";
import { usePlaidLink } from "react-plaid-link";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "sonner";
import meetingService from "../../services/meetingService";
import { ScheduleMeetingDialog, ProfessionalProfile } from "../ScheduleMeetingDialog";
import { InviteOthers } from "./InviteOthers";
import engagementService from "../../services/engagementService";
import { portfolioService } from "../../services/portfolioService";
import { CreateTransactionRequest } from "../../interface/CreateTransactionRequest";
import { TransactionResponse } from "../../interface/TransactionResponse";
import { transactionService } from "../../services/transactionService";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Progress } from "../ui/progress";
import {
  TrendingUp,
  TrendingDown,
  Download,
  Plus,
  ArrowUpDown,
  Wallet,
  PiggyBank,
  Heart,
  GraduationCap,
  Briefcase,
  Users,
  Shield,
  DollarSign,
  AlertCircle,
  FolderKanban,
  ChevronDown,
  ChevronRight,
  Target,
  PieChart as PieChartIcon,
  Pencil,
  Trash2,
  X,
  Loader2,
  Calendar,
  Clock,
  RefreshCw
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { AddAccount } from "./AddAccount";
import { AddStatement } from "./AddStatement";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart as RePieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import type { Account, Position } from "../../interface/Account";
import { ACCOUNT_TYPES, OWNERSHIP_TYPES, MEETING_TYPES } from "../../utils/financialTaxonomy";
import type { AccountTypeKey } from "../../utils/financialTaxonomy";
import {
  computeCumulativePortfolioReturn
} from "../../interface/AccountStatement";
import type { AccountStatement } from "../../interface/AccountStatement";
import type { BenchmarkMonthlyReturn } from "../../interface/BenchmarkMonthlyReturn";

const transactions = [];

const sectorAllocation = [];

// Chart data for performance visualization (cumulative percentage return)
const portfolioPerformanceData = [];

// Computed portfolio performance data derived from sampleAccountStatements (Jan-Jun 2026)
// Monthly Return = ((endingValue - (deposits - withdrawals - fees + dividendsEarned + interestEarned)) / startingValue - 1) * 100
// Cumulative return compounds monthly TWR across both accounts
//we will upload recent trades from csv file
const recentTrades = [
  /* { symbol: "AAPL", type: "Buy", shares: 500, price: 185.50, date: "Jun 15", account: "Main Brokerage" },
  { symbol: "VTI", type: "Buy", shares: 450, price: 250.00, date: "Jun 12", account: "Roth IRA" },
  { symbol: "SPY", type: "Buy", shares: 100, price: 480.00, date: "Jun 10", account: "401(k)" },
  { symbol: "NVDA", type: "Buy", shares: 200, price: 621.10, date: "Jun 8", account: "Joint Account" },
 */];

const sampleAccounts: Account[] = [
];

export function InvestorPortfolio() {
  const { userId } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>(sampleAccounts);
  const [isAddingAccount, setIsAddingAccount] = useState(false);
  const [isAddingActivity, setIsAddingActivity] = useState(false);
  const [meetings, setMeetings] = useState<any[]>([]);
  const [isScheduleMeetingOpen, setIsScheduleMeetingOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState<ProfessionalProfile | null>(null);
  const [connectedProfessionals, setConnectedProfessionals] = useState<ProfessionalProfile[]>([]);
  const [isAddingStatement, setIsAddingStatement] = useState(false);
  const [expandedAccounts, setExpandedAccounts] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [recentTransactions, setRecentTransactions] = useState<TransactionResponse[]>([]);
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(false);
  const [transactionError, setTransactionError] = useState<string | null>(null);
  const [computedPerformanceData, setComputedPerformanceData] = useState<
    { month: string; portfolio: number; benchmark: number; nasdaq: number; dow: number }[]
  >([]);

  const [statementList, setStatementList] = useState<AccountStatement[]>([]);
  const [isLoadingStatementList, setIsLoadingStatementList] = useState(false);
  const [isSyncingStatements, setIsSyncingStatements] = useState(false);
  const [statementListError, setStatementListError] = useState<string | null>(null);
  const [statementFilters, setStatementFilters] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 6);
    return {
      startDate: start.toISOString().split("T")[0],
      endDate: end.toISOString().split("T")[0],
      periodType: "MONTHLY",
    };
  });
  const [editingStatement, setEditingStatement] = useState<AccountStatement | null>(null);
  const [deletingStatementId, setDeletingStatementId] = useState<string | null>(null);
  const [plaidConsentToken, setPlaidConsentToken] = useState<string | null>(null);

  const { open: openPlaidConsent, ready: plaidConsentReady, error: plaidConsentError } = usePlaidLink({
    token: plaidConsentToken,
    onSuccess: () => {
      setPlaidConsentToken(null);
      toast.success("Statement access granted");
      void handleSyncPlaidStatements();
    },
    onExit: (err) => {
      setPlaidConsentToken(null);
      if (err) {
        const detail = [err.error_code, err.error_message, err.request_id]
          .filter(Boolean)
          .join(" · ");
        toast.error("Plaid consent failed", { description: detail || undefined });
      }
    },
    onEvent: (eventName, metadata) => {
      if (eventName === "ERROR") {
        console.error("Plaid Link error event", metadata);
      }
    },
  });

  useEffect(() => {
    if (plaidConsentToken && plaidConsentReady) {
      openPlaidConsent();
    }
  }, [plaidConsentToken, plaidConsentReady, openPlaidConsent]);

  useEffect(() => {
    if (plaidConsentError) {
      toast.error("Plaid Link failed to initialize", {
        description: plaidConsentError.message,
      });
    }
  }, [plaidConsentError]);

  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);
  const [editingPositionAccountId, setEditingPositionAccountId] = useState<string | null>(null);
  const [deletingPosition, setDeletingPosition] = useState<Position | null>(null);
  const [deletingPositionAccountId, setDeletingPositionAccountId] = useState<string | null>(null);
  const [newPosition, setNewPosition] = useState({ symbol: "", name: "", shares: "", avgCost: "", sector: "" });
  const [deletedPositionIndices, setDeletedPositionIndices] = useState<number[]>([]);
  const [isSavingAccount, setIsSavingAccount] = useState(false);
  const [isSyncingAccounts, setIsSyncingAccounts] = useState(false);

  const [activityForm, setActivityForm] = useState({
    transactionDate: "",
    accountNumber: "",
    transactionType: "",
    action: "",
    symbol: "",
    payment: "Cash",
    currency: "USD",
    price: "",
    quantity: "",
    commission: "",
    fees: "",
    interest: "",
    amount: "",
  });

  const handleActivityFormChange = (field: string, value: string) => {
    setActivityForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddActivity = async () => {
    const account = accounts.find((a) => a.accountNumber === activityForm.accountNumber);
    if (!account) return;

    const payload: CreateTransactionRequest = {
      accountId: account.id,
      transactionType: activityForm.transactionType,
      transactionDate: activityForm.transactionDate,
      securitySymbol: activityForm.symbol || undefined,
      quantity: activityForm.quantity ? Number(activityForm.quantity) : undefined,
      price: activityForm.price ? Number(activityForm.price) : undefined,
      amount: activityForm.amount ? Number(activityForm.amount) : (Number(activityForm.price) || 0) * (Number(activityForm.quantity) || 0),
      fees: activityForm.fees ? Number(activityForm.fees) : undefined,
      commission: activityForm.commission ? Number(activityForm.commission) : undefined,
      interest: activityForm.interest ? Number(activityForm.interest) : undefined,
      currency: activityForm.currency || undefined,
      description: activityForm.action || undefined,
      source: "manual",
    };

    try {
      await transactionService.createTransaction(userId, payload);
      setIsAddingActivity(false);
      setActivityForm({
        transactionDate: "",
        accountNumber: "",
        transactionType: "",
        action: "",
        symbol: "",
        payment: "Cash",
        currency: "USD",
        price: "",
        quantity: "",
        commission: "",
        fees: "",
        interest: "",
        amount: "",
      });
      await Promise.all([fetchPortfolios(), fetchTransactions()]);
    } catch (error) {
      setTransactionError(error instanceof Error ? error.message : "Failed to create transaction");
    }
  };

  const fetchPortfolios = async () => {
    if (!userId) return;
    try {
      const response = await portfolioService.getAllPortfoliosByUserIdForInvestor(userId);
      const data = Array.isArray(response) ? response : (response?.data || []);
      if (data.length > 0) {
        const mapped = mapApiAccounts(data);
        setAccounts(mapped);
      }
    } catch (error) {
      console.error("Failed to fetch portfolios:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncPlaidAccounts = async () => {
    if (!userId) return;
    setIsSyncingAccounts(true);
    try {
      await portfolioService.syncPlaidAccounts(userId);
      await fetchPortfolios();
      toast.success("Plaid accounts refreshed");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to sync Plaid accounts");
    } finally {
      setIsSyncingAccounts(false);
    }
  };

  const handleEditAccount = async (account: Account) => {
    setIsSavingAccount(true);
    try {
      const { id, ...accountData } = account;
      const payload = {
        ...accountData,
        accountType: account.accountType.toLowerCase(),
        ownershipType: account.ownershipType.toLowerCase(),
        status: account.status.toLowerCase(),
        positions: (account.positions || []).map((pos, idx) => ({
          ...pos,
          delete: deletedPositionIndices.includes(idx),
        })),
      };
      await portfolioService.updatePortfolio(id, payload);
      await fetchPortfolios();
      setEditingAccount(null);
      setDeletedPositionIndices([]);
    } catch (error) {
      console.error("Failed to update portfolio:", error);
    } finally {
      setIsSavingAccount(false);
    }
  };

  const handleEditPosition = async (accountId: string, position: Position) => {
    try {
      await portfolioService.updatePortfolioPosition(accountId, position);
      await fetchPortfolios();
      setEditingPosition(null);
      setEditingPositionAccountId(null);
    } catch (error) {
      console.error("Failed to update position:", error);
    }
  };

  const handleAddPositionToEdit = () => {
    if (!newPosition.symbol || !newPosition.shares || !newPosition.avgCost || !editingAccount) return;
    const position: Position = {
      id: "",
      symbol: newPosition.symbol.toUpperCase(),
      name: newPosition.name || newPosition.symbol,
      shares: parseFloat(newPosition.shares),
      avgCost: parseFloat(newPosition.avgCost),
      price: parseFloat(newPosition.avgCost),
      value: parseFloat(newPosition.shares) * parseFloat(newPosition.avgCost),
      change: 0,
      totalReturn: 0,
      sector: newPosition.sector || "Diversified",
      delete: false,
    };
    setEditingAccount({ ...editingAccount, positions: [...(editingAccount.positions || []), position] });
    setNewPosition({ symbol: "", name: "", shares: "", avgCost: "", sector: "" });
  };

  const handleRemovePositionFromEdit = (index: number) => {
    if (!editingAccount) return;
    setDeletedPositionIndices((prev) => [...prev, index]);
    setEditingAccount({
      ...editingAccount,
      positions: (editingAccount.positions || []).map((pos, i) =>
        i === index ? { ...pos, delete: true } : pos
      ),
    });
  };

  const handleDeletePosition = async (accountId: string, position: Position) => {
    try {
      await portfolioService.deletePortfolioPosition(accountId, position.id);
      await fetchPortfolios();
      setDeletingPosition(null);
      setDeletingPositionAccountId(null);
    } catch (error) {
      console.error("Failed to delete position:", error);
    }
  };

  const fetchTransactions = async () => {
    if (!userId) return;
    setIsTransactionsLoading(true);
    try {
      const response: any = await transactionService.getRecentTransactions(userId);
      const data = Array.isArray(response) ? response : (response?.data || []);
      setRecentTransactions(data);
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    } finally {
      setIsTransactionsLoading(false);
    }
  };

  const fetchStatements = async (filters?: { startDate: string; endDate: string; periodType: string }) => {
    if (!userId) return;
    setIsLoadingStatementList(true);
    setStatementListError(null);
    try {
      const effectiveFilters = filters || statementFilters;
      const res: any = await portfolioService.getAllAccountStatements(userId, {
        startDate: effectiveFilters.startDate,
        endDate: effectiveFilters.endDate,
        periodType: effectiveFilters.periodType,
      });
      const data = Array.isArray(res) ? res : (res?.data || []);
      setStatementList(data);
    } catch (error) {
      setStatementListError(error instanceof Error ? error.message : "Failed to load statements");
    } finally {
      setIsLoadingStatementList(false);
    }
  };

  const scheduleStatementRefresh = () => {
    [4000, 10000, 20000, 40000].forEach((delay) =>
      window.setTimeout(() => {
        void fetchStatements();
      }, delay)
    );
  };

  const startStatementsConsent = async (itemId: string) => {
    try {
      const res: any = await portfolioService.createPlaidStatementsConsentLinkToken(itemId);
      const token = res?.linkToken;
      if (!token) throw new Error("No link token returned");
      setPlaidConsentToken(token);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not start statement consent");
    }
  };

  const handleSyncPlaidStatements = async () => {
    if (!userId) return;
    setIsSyncingStatements(true);
    try {
      const res: any = await portfolioService.syncPlaidStatements(userId);
      await fetchStatements();
      const pending: string[] = res?.itemsRequiringConsent ?? [];
      if (pending.length > 0) {
        toast.warning("Statement access needed", {
          description:
            "Your linked institution needs to grant Plaid permission to share statements.",
          action: {
            label: "Grant access",
            onClick: () => startStatementsConsent(pending[0]),
          },
        });
      } else {
        toast.success("Plaid statements synced");
        scheduleStatementRefresh();
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to sync Plaid statements");
    } finally {
      setIsSyncingStatements(false);
    }
  };

  const handleDownloadPlaidStatement = async (stmt: AccountStatement) => {
    if (!stmt.externalStatementId) return;
    try {
      await portfolioService.downloadPlaidStatement(stmt.externalStatementId);
      await fetchStatements();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to download statement");
    }
  };

  const handleDeleteStatement = async (statementId: string) => {
    try {
      await portfolioService.deleteAccountStatement(statementId);
      setDeletingStatementId(null);
      fetchStatements();
    } catch (error) {
      console.error("Failed to delete statement:", error);
    }
  };

  const handleSaveEditedStatement = async () => {
    if (!editingStatement) return;
    try {
      await portfolioService.updateAccountStatement(editingStatement.id, editingStatement);
      setEditingStatement(null);
      fetchStatements();
    } catch (error) {
      console.error("Failed to update statement:", error);
    }
  };

  useEffect(() => {
    fetchPortfolios();
  }, [userId]);

  useEffect(() => {
    fetchTransactions();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetchStatements();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    const fetchPerformanceData = async () => {
      try {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setFullYear(startDate.getFullYear() - 1);

        const request = {
          startDate: startDate.toISOString().split("T")[0],
          endDate: endDate.toISOString().split("T")[0],
          periodType: "MONTHLY",
        };

        const [statementsRes, sp500Res, nasdaqRes, dowRes] = await Promise.all([
          portfolioService.getAllAccountStatements(userId, {
            startDate: request.startDate,
            endDate: request.endDate,
            periodType: request.periodType,
          }),
          portfolioService.getMonthlyBenchmarkReturn({
            startDate: request.startDate,
            endDate: request.endDate,
            benchmarkName: "S&P 500",
          }),
          portfolioService.getMonthlyBenchmarkReturn({
            startDate: request.startDate,
            endDate: request.endDate,
            benchmarkName: "NASDAQ",
          }),
          portfolioService.getMonthlyBenchmarkReturn({
            startDate: request.startDate,
            endDate: request.endDate,
            benchmarkName: "DOW",
          }),
        ]);

        const extractData = <T,>(res: any): T[] =>
          Array.isArray(res) ? res : (res && typeof res === 'object' && 'data' in res ? (res as any).data : []);

        const statements: AccountStatement[] = extractData<AccountStatement>(statementsRes)
          .filter((s) => s.startingValue != null && s.endingValue != null);
        const sp500Benchmarks: BenchmarkMonthlyReturn[] = extractData(sp500Res);
        const nasdaqBenchmarks: BenchmarkMonthlyReturn[] = extractData(nasdaqRes);
        const dowBenchmarks: BenchmarkMonthlyReturn[] = extractData(dowRes);

        const data = computeCumulativePortfolioReturn(statements, sp500Benchmarks, nasdaqBenchmarks, dowBenchmarks);
        setComputedPerformanceData(data);
      } catch (error) {
        console.error("Failed to fetch performance data:", error);
      }
    };

    fetchPerformanceData();
  }, [userId]);

  const fetchMeetings = async () => {
    if (!userId) return;
    try {
      const [pendingRes, acceptedRes] = await Promise.all([
        meetingService.getMyMeetings(userId, "PENDING", false, ''),
        meetingService.getMyMeetings(userId, "ACCEPTED", false, '')
      ]);
      const pMeetings = pendingRes.data || (Array.isArray(pendingRes) ? pendingRes : []);
      const aMeetings = acceptedRes.data || (Array.isArray(acceptedRes) ? acceptedRes : []);
      const allMeetings = [...pMeetings, ...aMeetings].sort((a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );
      setMeetings(allMeetings);
    } catch (error) {
      console.error("Failed to fetch meetings:", error);
    }
  };

  const handleAcceptMeeting = async (meetingId) => {
    if (!userId) return;
    try { await meetingService.updateMeetingStatus(meetingId, userId, "ACCEPTED"); toast.success("Meeting accepted"); fetchMeetings(); }
    catch (e) { toast.error(e.message || "Failed to accept"); }
  };

  const handleDeclineMeeting = async (meetingId) => {
    if (!userId) return;
    try { await meetingService.updateMeetingStatus(meetingId, userId, "DECLINED"); toast.success("Meeting declined"); fetchMeetings(); }
    catch (e) { toast.error(e.message || "Failed to decline"); }
  };

  const handleCancelMeeting = async (meetingId) => {
    if (!userId) return;
    try { await meetingService.updateMeetingStatus(meetingId, userId, "CANCELLED"); toast.success("Meeting cancelled"); fetchMeetings(); }
    catch (e) { toast.error(e.message || "Failed to cancel"); }
  };

  const fetchConnectedProfessionals = async () => {
    if (!userId) return;
    try {
      const res = await engagementService.getMyAgreements(userId);
      const list = Array.isArray(res) ? res : (res.agreements || res.content || []);
      const signed = list.filter((a: any) => a.status === 'SIGNED_BY_BOTH' || a.status === 'SIGNED_BY_RECIPIENT' || a.status === 'PENDING');
      const uniqueMap = new Map<string, ProfessionalProfile>();
      signed.forEach((a: any) => {
        const profId = a.professionalId || a.recipientId;
        const profName = a.professionalName || a.recipientFullName;
        if (profId && profId !== userId && !uniqueMap.has(profId)) {
          uniqueMap.set(profId, { id: profId, name: profName || 'Professional' });
        }
      });
      setConnectedProfessionals(Array.from(uniqueMap.values()));
    } catch (e) {
      console.error("Failed to fetch professionals from agreements", e);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchMeetings();
      fetchConnectedProfessionals();
    }
  }, [userId]);

  const mapApiAccounts = (apiAccounts: any[]): Account[] => {
    return apiAccounts.map((acc: any) => ({
      id: acc.id || String(Math.random()),
      accountNumber: acc.accountNumber || "",
      custodian: acc.custodian || "",
      accountNumberCustodian: acc.accountNumberCustodian || "",
      accountType: mapAccountTypeKey(acc.accountType),
      ownershipType: mapOwnershipTypeKey(acc.ownershipType),
      accountName: acc.accountName || "Account",
      balance: acc.balance ?? 0,
      cashBalance: acc.cashBalance ?? 0,
      ytdReturn: acc.ytdReturn ?? 0,
      contributions: acc.contributions ?? 0,
      status: mapStatus(acc.status),
      openedDate: acc.openedDate || "",
      taxBenefits: acc.taxBenefits || [],
      restrictions: acc.restrictions || [],
      source: acc.source || "manual",
      externalRef: acc.externalRef,
      institutionId: acc.institutionId,
      institutionName: acc.institutionName,
      mask: acc.mask,
      availableBalance: acc.availableBalance,
      balanceAsOf: acc.balanceAsOf,
      linkStatus: acc.linkStatus,
      lastSyncedAt: acc.lastSyncedAt,
      positions: (acc.positions || []).map((pos: any) => ({
        id: pos.id || "",
        symbol: pos.symbol || "",
        name: pos.name || "",
        shares: pos.shares ?? 0,
        price: pos.currentPrice ?? pos.price ?? 0,
        avgCost: pos.avgCost ?? 0,
        value: pos.marketValue ?? pos.value ?? 0,
        change: pos.change ?? 0,
        totalReturn: pos.gainLossPercent ?? pos.totalReturn ?? 0,
        sector: pos.sector || "",
        assetType: pos.assetType,
        securityId: pos.securityId,
        currencyCode: pos.currencyCode,
        isCashEquivalent: pos.isCashEquivalent,
        vestedQuantity: pos.vestedQuantity,
        vestedValue: pos.vestedValue,
      })),
    }));
  };

  const mapAccountTypeKey = (type: string): AccountTypeKey => {
    const key = type?.toUpperCase().replace(/[\s-]/g, "_");
    if (key && key in ACCOUNT_TYPES) return key as AccountTypeKey;
    return "BROKERAGE";
  };

  const mapOwnershipTypeKey = (type: string): keyof typeof OWNERSHIP_TYPES => {
    const key = type?.toUpperCase().replace(/[\s-]/g, "_");
    if (key && key in OWNERSHIP_TYPES) return key as keyof typeof OWNERSHIP_TYPES;
    return "INDIVIDUAL";
  };

  const mapStatus = (status: string): "Active" | "Pending" | "Closed" => {
    const s = status?.toLowerCase();
    if (s === "active") return "Active";
    if (s === "pending") return "Pending";
    return "Closed";
  };

  // Calculate totals from all positions across all accounts
  const isCashEquivalentPosition = (position: Position) =>
    position.assetType?.toUpperCase() === "CASH";

  const allPositions = accounts.flatMap(acc => acc.positions || []);
  const totalValue = allPositions.reduce((sum, p) => sum + p.value, 0);
  const totalCost = allPositions.reduce(
    (sum, p) => sum + (isCashEquivalentPosition(p) ? p.value : p.avgCost * p.shares),
    0
  );
  const totalReturn = totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : 0;
  const dayChangeAmount = allPositions.reduce((sum, p) => sum + (p.change / 100) * p.value, 0);
  const yesterdayValue = totalValue - dayChangeAmount;
  const dayChangePercent = yesterdayValue > 0 ? (dayChangeAmount / yesterdayValue) * 100 : 0;

  // Account calculations
  const totalAccountBalance = accounts
    .filter(a => a.status === "Active")
    .reduce((sum, a) => sum + a.balance, 0);
  
  const totalContributions = accounts
    .filter(a => a.status === "Active")
    .reduce((sum, a) => sum + a.contributions, 0);
  
  const avgReturn = computedPerformanceData.length > 0
    ? computedPerformanceData[computedPerformanceData.length - 1].portfolio
    : accounts.filter(a => a.status === "Active")
        .reduce((sum, a) => sum + a.ytdReturn, 0) / accounts.filter(a => a.status === "Active").length || 0;

  // Group accounts by category
  const accountsByCategory = accounts.reduce((acc, account) => {
    const accountTypeInfo = ACCOUNT_TYPES[account.accountType];
    if (!accountTypeInfo) return acc;
    const category = accountTypeInfo.category;
    if (!acc[category]) acc[category] = [];
    acc[category].push(account);
    return acc;
  }, {} as Record<string, Account[]>);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "Taxable": return Wallet;
      case "Retirement": return PiggyBank;
      case "Education": return GraduationCap;
      case "Health": return Heart;
      case "Cash": return Wallet;
      default: return Briefcase;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Taxable": return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
      case "Retirement": return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300";
      case "Education": return "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300";
      case "Health": return "bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300";
      case "Cash": return "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300";
      default: return "bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  // Calculate sector allocation from all positions
  const sectorTotals = allPositions.reduce((acc, pos) => {
    const sector = isCashEquivalentPosition(pos)
      ? "Cash & Equivalents"
      : pos.sector || "Broad Market";
    acc[sector] = (acc[sector] || 0) + pos.value;
    return acc;
  }, {} as Record<string, number>);

  const sectorAllocationData = Object.entries(sectorTotals).map(([sector, value]) => ({
    sector,
    value: (value / totalValue) * 100,
    amount: value,
  })).sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-6">
      {isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading portfolio...
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl">Portfolio Overview</h2>
          <p className="text-muted-foreground">
            Your complete financial dashboard and holdings
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
            value={selectedProfessional?.id || ''}
            onChange={(e) => {
              const prof = connectedProfessionals.find((p) => p.id === e.target.value);
              setSelectedProfessional(prof || null);
            }}
          >
            <option value="">Select professional...</option>
            {connectedProfessionals.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <Button onClick={() => setIsScheduleMeetingOpen(true)} disabled={!selectedProfessional}>
            Schedule Meeting
          </Button>
          <Button variant="outline" onClick={() => setIsInviteOpen(true)}>
            <Users className="mr-2 h-4 w-4" />
            Invite Others
          </Button>
        </div>
      </div>

      {/* Key Metrics Summary */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Total Portfolio Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">${(totalAccountBalance / 1000).toFixed(0)}K</div>
            <p className="text-xs text-green-600">+${(totalAccountBalance * 0.125 / 1000).toFixed(0)}K this month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Total Return</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl ${totalReturn >= 0 ? "text-green-600" : "text-red-600"}`}>{totalReturn >= 0 ? "+" : ""}{totalReturn.toFixed(2)}%</div>
            <p className={`text-xs ${totalValue - totalCost >= 0 ? "text-green-600" : "text-red-600"}`}>${Math.abs(totalValue - totalCost) >= 1000 ? `${((totalValue - totalCost) / 1000).toFixed(2)}K` : (totalValue - totalCost).toFixed(2)} {totalValue - totalCost >= 0 ? "gain" : "loss"}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Day's Gain/Loss</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl ${dayChangeAmount >= 0 ? "text-green-600" : "text-red-600"}`}>
              {dayChangeAmount >= 0 ? "+" : ""}${Math.abs(dayChangeAmount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
            <p className={`text-xs ${dayChangePercent >= 0 ? "text-green-600" : "text-red-600"}`}>
              {dayChangePercent >= 0 ? "+" : ""}{dayChangePercent.toFixed(2)}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">YTD Contributions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">${(totalContributions / 1000).toFixed(0)}K</div>
            <p className="text-xs text-muted-foreground">This year</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Accounts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{accounts.filter(a => a.status === "Active").length}</div>
            <p className="text-xs text-muted-foreground">{allPositions.length} positions</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Avg Account Return</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl ${avgReturn >= 0 ? "text-green-600" : "text-red-600"}`}>{avgReturn >= 0 ? "+" : ""}{avgReturn.toFixed(2)}%</div>
            <p className="text-xs text-muted-foreground">YTD weighted avg</p>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Meetings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Upcoming Meetings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {meetings.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
                <p>No upcoming meetings</p>
              </div>
            ) : (
              meetings.map((meeting) => {
                const dateStr = meeting.startTime.endsWith('Z') || meeting.startTime.includes('+')
                  ? meeting.startTime
                  : `${meeting.startTime}Z`;
                const formattedTime = new Date(dateStr).toLocaleString([], {
                  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                });
                const clientName = meeting.hostId === userId
                  ? meeting.participantFullName || meeting.participantId
                  : meeting.hostFullName || meeting.hostId;
                return (
                  <div key={meeting.meetingId || meeting.id} className="flex items-center justify-between border-b pb-3 last:border-0 last:pb-0">
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{clientName}</div>
                        <div className="text-xs text-muted-foreground">{MEETING_TYPES.find(mt => mt.value === meeting.meetingType?.toLowerCase())?.label || meeting.title || "Meeting"}</div>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div>
                        <div className="text-sm">{formattedTime}</div>
                        <Badge variant={meeting.status === "ACCEPTED" ? "default" : "secondary"} className="text-xs mt-1">
                          {meeting.status === "ACCEPTED" ? "Accepted" : meeting.status === "PENDING" ? "Pending" : meeting.status}
                        </Badge>
                      </div>
                      {meeting.status === "PENDING" && meeting.hostId !== userId && (
                        <div className="flex flex-col gap-1">
                          <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={() => handleAcceptMeeting(meeting.meetingId || meeting.id)}>Accept</Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" onClick={() => handleDeclineMeeting(meeting.meetingId || meeting.id)}>Decline</Button>
                        </div>
                      )}
                      {meeting.status === "ACCEPTED" && (
                        <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" onClick={() => handleCancelMeeting(meeting.meetingId || meeting.id)}>Cancel</Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Performance Chart and Account Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Portfolio Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={computedPerformanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="portfolio"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="#3b82f6"
                  fillOpacity={0.2}
                  name="Your Portfolio"
                />
                <Area
                  type="monotone"
                  dataKey="benchmark"
                  stroke="#94a3b8"
                  strokeWidth={2}
                  fill="#94a3b8"
                  fillOpacity={0.1}
                  name="S&P 500"
                />
                <Area
                  type="monotone"
                  dataKey="nasdaq"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  fill="#f59e0b"
                  fillOpacity={0.05}
                  name="NASDAQ"
                />
                <Area
                  type="monotone"
                  dataKey="dow"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="#10b981"
                  fillOpacity={0.05}
                  name="Dow Jones"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Account Type Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Accounts by Type</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(accountsByCategory).map(([category, categoryAccounts]) => {
              const Icon = getCategoryIcon(category);
              const categoryBalance = categoryAccounts.reduce((sum, a) => sum + a.balance, 0);
              
              return (
                <div key={category} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${getCategoryColor(category)}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-sm">{category}</div>
                      <div className="text-xs text-muted-foreground">
                        {categoryAccounts.length} {categoryAccounts.length === 1 ? 'account' : 'accounts'}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm">${(categoryBalance / 1000).toFixed(0)}K</div>
                    <div className="text-xs text-muted-foreground">
                      {((categoryBalance / totalAccountBalance) * 100).toFixed(0)}%
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Sector Allocation and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sector Allocation Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Sector Allocation</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <RePieChart>
                <Pie
                  data={sectorAllocationData.map((sector, idx) => ({
                    name: sector.sector,
                    value: sector.value,
                    fill: ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'][idx % 7]
                  }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value.toFixed(1)}%`}
                  outerRadius={96}
                  dataKey="value"
                >
                  {sectorAllocationData.map((entry, index) => (
                    <Cell key={`cell-${index}`} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
              </RePieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Recent Trades */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Activity</CardTitle>
              <Button variant="outline" size="sm" onClick={() => setIsAddingActivity(true)}>
                <Plus className="mr-1 h-4 w-4" />
                Add Transaction Activity
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isTransactionsLoading ? (
              <div className="text-center text-muted-foreground py-4">Loading transactions...</div>
            ) : recentTransactions.length === 0 ? (
              <div className="text-center text-muted-foreground py-4">No recent transactions</div>
            ) : (
            <div className="space-y-4">
              {recentTransactions.map((trade, idx) => (
                <div key={trade.transactionId || idx} className="flex items-center justify-between border-b pb-3 last:border-0 last:pb-0">
                  <div className="flex items-center gap-3">
                    <Badge variant={trade.transactionType === "BUY" ? "default" : "secondary"}>
                      {trade.transactionType}
                    </Badge>
                    <div>
                      <div className="font-medium">{trade.securitySymbol}</div>
                      <div className="text-xs text-muted-foreground">{trade.accountName}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm">
                      {trade.quantity ? `${Math.abs(trade.quantity).toFixed(trade.quantity % 1 === 0 ? 0 : 3)} shares` : `$${trade.amount.toFixed(2)}`}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {trade.price ? `$${trade.price.toFixed(2)}` : ""}{trade.price && trade.transactionDate ? " • " : ""}{trade.transactionDate ? new Date(trade.transactionDate).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
          </CardContent>
        </Card>

        {/* Add Activity Dialog */}
        <Dialog open={isAddingActivity} onOpenChange={(open) => { setIsAddingActivity(open); if (!open) setTransactionError(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add Transaction Activity</DialogTitle>
              <DialogDescription>
                Record a new transaction activity
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Transaction Date</Label>
                  <Input type="date" value={activityForm.transactionDate} onChange={(e) => handleActivityFormChange("transactionDate", e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label>Account Number</Label>
                  <Select value={activityForm.accountNumber} onValueChange={(value) => handleActivityFormChange("accountNumber", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map((account) => (
                        <SelectItem key={account.id} value={account.accountNumber}>
                          {account.accountNumber} - {account.accountName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Transaction Type</Label>
                  <Select value={activityForm.transactionType} onValueChange={(value) => handleActivityFormChange("transactionType", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BUY">BUY</SelectItem>
                      <SelectItem value="SELL">SELL</SelectItem>
                      <SelectItem value="REINVEST">REINVEST</SelectItem>
                      <SelectItem value="DIVIDEND">DIVIDEND</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Action</Label>
                  <Input placeholder="e.g., YOU BOUGHT..." value={activityForm.action} onChange={(e) => handleActivityFormChange("action", e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Symbol</Label>
                  {activityForm.transactionType === "BUY" || !activityForm.transactionType ? (
                    <Input placeholder="e.g., AAPL" value={activityForm.symbol} onChange={(e) => handleActivityFormChange("symbol", e.target.value)} />
                  ) : (
                    <Select value={activityForm.symbol} onValueChange={(value) => handleActivityFormChange("symbol", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select symbol" />
                      </SelectTrigger>
                      <SelectContent>
                        {accounts
                          .find((a) => a.accountNumber === activityForm.accountNumber)
                          ?.positions?.map((pos) => (
                            <SelectItem key={pos.symbol} value={pos.symbol}>
                              {pos.symbol} - {pos.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label>Payment</Label>
                  <Input placeholder="Cash" value={activityForm.payment} onChange={(e) => handleActivityFormChange("payment", e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Currency</Label>
                  <Input placeholder="USD" value={activityForm.currency} onChange={(e) => handleActivityFormChange("currency", e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label>Amount</Label>
                  <Input type="number" step="0.01" placeholder="0.00" value={activityForm.amount} onChange={(e) => handleActivityFormChange("amount", e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Price</Label>
                  <Input type="number" step="0.01" placeholder="0.00" value={activityForm.price} onChange={(e) => handleActivityFormChange("price", e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label>Quantity</Label>
                  <Input type="number" step="0.001" placeholder="0" value={activityForm.quantity} onChange={(e) => handleActivityFormChange("quantity", e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Commission</Label>
                  <Input type="number" step="0.01" placeholder="0.00" value={activityForm.commission} onChange={(e) => handleActivityFormChange("commission", e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label>Fees</Label>
                  <Input type="number" step="0.01" placeholder="0.00" value={activityForm.fees} onChange={(e) => handleActivityFormChange("fees", e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Accrued Interest</Label>
                  <Input type="number" step="0.01" placeholder="0.00" value={activityForm.interest} onChange={(e) => handleActivityFormChange("interest", e.target.value)} />
                </div>
              </div>
            </div>
            {transactionError && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{transactionError}</span>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddingActivity(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddActivity}>
                Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="accounts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="accounts">Accounts & Positions</TabsTrigger>
          <TabsTrigger value="allocation">Allocation</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="statements">Statements</TabsTrigger>
        </TabsList>

        {/* Accounts Tab */}
        <TabsContent value="accounts" className="space-y-4">
          <>
          {/* Add Account Button */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleSyncPlaidAccounts} disabled={isSyncingAccounts}>
              {isSyncingAccounts ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Refresh from Plaid
            </Button>
            <Button onClick={() => setIsAddingAccount(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Account
            </Button>
            <AddAccount
              open={isAddingAccount}
              onClose={() => setIsAddingAccount(false)}
              onAccountsAdd={(newAccounts) => setAccounts(prev => [...prev, ...newAccounts])}
              userId={userId}
            />
          </div>

          {/* Accounts by Category */}
          {Object.entries(accountsByCategory).map(([category, categoryAccounts]) => {
            const Icon = getCategoryIcon(category);
            const categoryBalance = categoryAccounts.reduce((sum, a) => sum + a.balance, 0);
            
            return (
              <Card key={category}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${getCategoryColor(category)}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle>{category} Accounts</CardTitle>
                        <p className="text-sm text-muted-foreground">
                          {categoryAccounts.length} accounts • ${(categoryBalance / 1000).toFixed(0)}K total
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12 px-2"></TableHead>
                        <TableHead className="px-4 text-left">Account #</TableHead>
                        <TableHead className="px-4 text-left">Custodian</TableHead>
                        <TableHead className="px-4 text-left">Custodian <br />Account #</TableHead>
                        <TableHead className="px-4 text-left">Type</TableHead>
                        <TableHead className="px-4 text-left">Ownership</TableHead>
                        <TableHead className="text-right px-4">Balance</TableHead>
                        <TableHead className="text-right px-4">Cash</TableHead>
                        <TableHead className="text-right px-4">YTD <br />Contributions</TableHead>
                        <TableHead className="text-left px-4">Status</TableHead>
                        <TableHead className="text-left px-4">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryAccounts.map((account) => (
                        <Collapsible
                          key={account.id}
                          asChild
                          open={expandedAccounts[account.id]}
                          onOpenChange={(open) => 
                            setExpandedAccounts({ ...expandedAccounts, [account.id]: open })
                          }
                        >
                          <>
                          <TableRow className="border-b">
                            <TableCell className="w-12 px-2">
                              <CollapsibleTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  {expandedAccounts[account.id] ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              </CollapsibleTrigger>
                            </TableCell>
                            
                            <TableCell className="px-4">
                              <div className="font-medium">{account.accountName}</div>
                              <div className="text-xs text-muted-foreground">
                                {account.accountNumber} • {account.positions?.length || 0} positions
                              </div>
                            </TableCell>
                            
                            <TableCell className="px-4">
                              <span className="text-sm">{account.custodian || "-"}</span>
                            </TableCell>
                            
                            <TableCell className="px-4">
                              <span className="text-sm">{account.accountNumberCustodian || "-"}</span>
                            </TableCell>
                            
                            <TableCell className="px-4">
                              <Badge variant="outline">
                                {ACCOUNT_TYPES[account.accountType].name}
                              </Badge>
                            </TableCell>
                            
                            <TableCell className="text-sm text-muted-foreground px-4">
                              {OWNERSHIP_TYPES[account.ownershipType]}
                            </TableCell>
                            
                            <TableCell className="text-right px-4">
                              ${account.balance.toLocaleString()}
                            </TableCell>
                            
                            <TableCell className="text-right px-4">
                              ${account.cashBalance.toLocaleString()}
                            </TableCell>
                            
                            <TableCell className="text-right px-4">
                              ${account.contributions.toLocaleString()}
                            </TableCell>
                            
                            <TableCell className="px-4">
                              <Badge variant={account.status === "Active" ? "secondary" : "outline"}>
                                {account.status}
                              </Badge>
                            </TableCell>
                            
                            <TableCell className="px-4">
                              <div className="flex gap-1">
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      Details
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-3xl">
                                    <DialogHeader>
                                      <DialogTitle>{account.accountName}</DialogTitle>
                                      <DialogDescription>
                                        {ACCOUNT_TYPES[account.accountType].name} • {OWNERSHIP_TYPES[account.ownershipType]}
                                      </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                      <div className="grid grid-cols-2 gap-4">
                                        <div>
                                          <Label className="text-xs text-muted-foreground">Account Number</Label>
                                          <p className="text-sm">{account.accountNumber}</p>
                                        </div>
                                        <div>
                                          <Label className="text-xs text-muted-foreground">Opened Date</Label>
                                          <p className="text-sm">{account.openedDate}</p>
                                        </div>
                                        <div>
                                          <Label className="text-xs text-muted-foreground">Current Balance</Label>
                                          <p className="text-sm">${account.balance.toLocaleString()}</p>
                                        </div>
                                        <div>
                                          <Label className="text-xs text-muted-foreground">YTD Return</Label>
                                          <p className="text-sm text-green-600">+{account.ytdReturn}%</p>
                                        </div>
                                      </div>

                                      {account.taxBenefits && account.taxBenefits.length > 0 && (
                                        <div>
                                          <Label className="text-sm flex items-center gap-2 mb-2">
                                            <DollarSign className="h-4 w-4" />
                                            Tax Benefits
                                          </Label>
                                          <ul className="space-y-1">
                                            {account.taxBenefits.map((benefit, idx) => (
                                              <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                                                <div className="h-1.5 w-1.5 rounded-full bg-green-500 mt-1.5" />
                                                {benefit}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}

                                      {account.restrictions && account.restrictions.length > 0 && (
                                        <div>
                                          <Label className="text-sm flex items-center gap-2 mb-2">
                                            <AlertCircle className="h-4 w-4" />
                                            Restrictions & Considerations
                                          </Label>
                                          <ul className="space-y-1">
                                            {account.restrictions.map((restriction, idx) => (
                                              <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                                                <div className="h-1.5 w-1.5 rounded-full bg-amber-500 mt-1.5" />
                                                {restriction}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  </DialogContent>
                                </Dialog>
                                <Button variant="outline" size="sm" onClick={() => setEditingAccount(account)}>
                                  <Pencil className="h-3.5 w-3.5 mr-1" />
                                  Edit
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          
                          {/* Positions Dropdown */}
                          <CollapsibleContent asChild>
                            <TableRow>
                              <TableCell colSpan={9} className="bg-muted/30 p-0">
                                {account.positions && account.positions.length > 0 ? (
                                  <div className="p-4">
                                    <div className="mb-2 text-sm font-medium text-muted-foreground">
                                      Positions in this account
                                    </div>
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Symbol</TableHead>
                                          <TableHead>Name</TableHead>
                                          <TableHead>Sector</TableHead>
                                          <TableHead className="text-right">Shares</TableHead>
                                          <TableHead className="text-right">Avg Cost</TableHead>
                                          <TableHead className="text-right">Total Cost</TableHead>
                                          <TableHead className="text-right">Current Price</TableHead>
                                          <TableHead className="text-right">Market Value</TableHead>
                                          <TableHead className="text-right">Today</TableHead>
                                          <TableHead className="text-right">Total Return</TableHead>
                                          <TableHead className="text-left px-2">Actions</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {account.positions.map((position) => (
                                          <TableRow key={position.symbol}>
                                            <TableCell>
                                              <a href={`https://finance.yahoo.com/quote/${position.symbol}/`} target="_blank" rel="noopener noreferrer" className="font-bold text-blue-600 hover:text-blue-800 hover:underline">{position.symbol}</a>
                                            </TableCell>
                                            <TableCell>{position.name}</TableCell>
                                            <TableCell>
                                              <span className="text-xs text-muted-foreground">
                                                {isCashEquivalentPosition(position)
                                                  ? "Cash & Equivalents"
                                                  : position.sector}
                                              </span>
                                            </TableCell>
                                            <TableCell className="text-right">{position.shares}</TableCell>
                                            <TableCell className="text-right">${position.avgCost.toFixed(2)}</TableCell>
                                            <TableCell className="text-right">${(position.shares * position.avgCost).toLocaleString()}</TableCell>
                                            <TableCell className="text-right">${position.price.toFixed(2)}</TableCell>
                                            <TableCell className="text-right">
                                              <div>
                                                <div>${position.value.toLocaleString()}</div>
                                                <div className={`text-xs ${position.totalReturn >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                  {position.totalReturn >= 0 ? "+" : ""}${(position.value - (position.avgCost * position.shares)).toLocaleString()}
                                                </div>
                                              </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                              <span className={`inline-flex items-center gap-1 ${position.change >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                {position.change >= 0 ? (
                                                  <TrendingUp className="h-3 w-3" />
                                                ) : (
                                                  <TrendingDown className="h-3 w-3" />
                                                )}
                                                {position.change >= 0 ? "+" : ""}
                                                {position.change.toFixed(2)}%
                                              </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                              <span className={`inline-flex items-center gap-1 ${position.totalReturn >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                {position.totalReturn >= 0 ? "+" : ""}
                                                {position.totalReturn.toFixed(2)}%
                                              </span>
                                            </TableCell>
                                            <TableCell className="px-2">
                                              <div className="flex gap-1">
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => {
                                                    setEditingPosition(position);
                                                    setEditingPositionAccountId(account.id);
                                                  }}
                                                >
                                                  <Pencil className="h-3.5 w-3.5 mr-1" />
                                                  Edit
                                                </Button>
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => {
                                                    setDeletingPosition(position);
                                                    setDeletingPositionAccountId(account.id);
                                                  }}
                                                >
                                                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                                                  Delete
                                                </Button>
                                              </div>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                ) : (
                                  <div className="p-4 text-sm text-muted-foreground text-center">
                                    No positions in this account
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          </CollapsibleContent>
                          </>
                        </Collapsible>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })}
          </>
        </TabsContent>

        {/* Allocation Tab */}
        <TabsContent value="allocation" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sector Allocation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {sectorAllocationData.map((sector) => (
                  <div key={sector.sector} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>{sector.sector}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-muted-foreground">
                          ${(sector.amount / 1000).toFixed(0)}K
                        </span>
                        <span className="font-medium">
                          {sector.value.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <Progress value={sector.value} className="h-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Allocation by Account Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(accountsByCategory).map(([category, categoryAccounts]) => {
                  const categoryBalance = categoryAccounts.reduce((sum, a) => sum + a.balance, 0);
                  const categoryPercent = (categoryBalance / totalAccountBalance) * 100;
                  const Icon = getCategoryIcon(category);
                  
                  return (
                    <div key={category} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded ${getCategoryColor(category)}`}>
                            <Icon className="h-3.5 w-3.5" />
                          </div>
                          <span>{category}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-muted-foreground">
                            ${(categoryBalance / 1000).toFixed(0)}K
                          </span>
                          <span className="font-medium">
                            {categoryPercent.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <Progress value={categoryPercent} className="h-2" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Recent Transactions</CardTitle>
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export History
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Symbol</TableHead>
                    <TableHead className="text-right">Shares</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentTransactions.map((tx, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{tx.transactionDate}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            tx.transactionType === "BUY" || tx.transactionType === "buy"
                              ? "default"
                              : tx.transactionType === "SELL" || tx.transactionType === "sell"
                              ? "destructive"
                              : "secondary"
                          }
                        >
                          {tx.transactionType}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{tx.securitySymbol}</TableCell>
                      <TableCell className="text-right">{tx.quantity || "-"}</TableCell>
                      <TableCell className="text-right">
                        {tx.price ? `$${tx.price.toFixed(2)}` : "-"}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        ${tx.amount.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Statements Tab */}
        <TabsContent value="statements" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Account Statements</CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSyncPlaidStatements}
                    disabled={isSyncingStatements}
                  >
                    {isSyncingStatements ? (
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-1 h-4 w-4" />
                    )}
                    Sync from Plaid
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setIsAddingStatement(true)}>
                    <Plus className="mr-1 h-4 w-4" />
                    Add Statement
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-end gap-4">
                <div className="space-y-2">
                  <Label>From</Label>
                  <Input
                    type="date"
                    value={statementFilters.startDate}
                    onChange={(e) =>
                      setStatementFilters({ ...statementFilters, startDate: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>To</Label>
                  <Input
                    type="date"
                    value={statementFilters.endDate}
                    onChange={(e) =>
                      setStatementFilters({ ...statementFilters, endDate: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Period</Label>
                  <Select
                    value={statementFilters.periodType}
                    onValueChange={(value) =>
                      setStatementFilters({ ...statementFilters, periodType: value })
                    }
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DAILY">Daily</SelectItem>
                      <SelectItem value="MONTHLY">Monthly</SelectItem>
                      <SelectItem value="YEARLY">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => fetchStatements()}>
                  Search
                </Button>
              </div>

              {isLoadingStatementList ? (
                <div className="flex items-center justify-center py-6 text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading statements...
                </div>
              ) : statementListError ? (
                <div className="text-sm text-red-600">{statementListError}</div>
              ) : statementList.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  No statements found
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12 px-2"></TableHead>
                        <TableHead className="text-left">Custodian Account #</TableHead>
                        <TableHead className="text-left">Custodian</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead className="text-right">Start Value</TableHead>
                        <TableHead className="text-right">End Value</TableHead>
                        <TableHead className="text-right">Return</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const grouped = statementList.reduce((acc, stmt) => {
                          if (!acc[stmt.accountId]) acc[stmt.accountId] = [];
                          acc[stmt.accountId].push(stmt);
                          return acc;
                        }, {} as Record<string, AccountStatement[]>);

                        return Object.entries(grouped).map(([accountId, stmts]) => {
                          const account = accounts.find((a) => a.id === accountId);
                          return (
                            <Collapsible
                              key={accountId}
                              asChild
                              open={expandedAccounts[accountId]}
                              onOpenChange={(open) =>
                                setExpandedAccounts({ ...expandedAccounts, [accountId]: open })
                              }
                            >
                              <>
                              <TableRow className="border-b">
                                <TableCell className="w-12 px-2">
                                  <CollapsibleTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                      {expandedAccounts[accountId] ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </CollapsibleTrigger>
                                </TableCell>
                                <TableCell className="font-medium">
                                  {account?.accountNumberCustodian || accountId}
                                </TableCell>
                                <TableCell>{account?.custodian || "-"}</TableCell>
                                <TableCell colSpan={5}>
                                  <span className="text-sm text-muted-foreground">
                                    {stmts.length} {stmts.length === 1 ? "statement" : "statements"}
                                  </span>
                                </TableCell>
                              </TableRow>

                              <CollapsibleContent asChild>
                                <TableRow>
                                  <TableCell colSpan={8} className="p-0">
                                    <div className="bg-muted/30">
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead>Period</TableHead>
                                            <TableHead className="text-right">Start Value</TableHead>
                                            <TableHead className="text-right">End Value</TableHead>
                                            <TableHead className="text-right">Deposits</TableHead>
                                            <TableHead className="text-right">Withdrawals</TableHead>
                                            <TableHead className="text-right">Return</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {stmts.map((stmt) => {
                                            const isPlaid = stmt.source === "PLAID";
                                            const hasValues =
                                              stmt.startingValue != null && stmt.endingValue != null;
                                            const netDeposits = hasValues
                                              ? (stmt.deposits ?? 0) - (stmt.withdrawals ?? 0) - (stmt.fees ?? 0) +
                                                (stmt.dividendsEarned ?? 0) + (stmt.interestEarned ?? 0)
                                              : 0;
                                            const twr =
                                              hasValues && stmt.startingValue > 0
                                                ? (isPlaid
                                                    ? ((stmt.endingValue - stmt.startingValue) / stmt.startingValue) * 100
                                                    : ((stmt.endingValue - netDeposits) / stmt.startingValue - 1) * 100)
                                                : null;
                                            return (
                                              <TableRow key={stmt.id}>
                                                <TableCell>
                                                  <div className="flex items-center gap-2">
                                                    {new Date(stmt.statementDate).toLocaleDateString("en-US", {
                                                      month: "short",
                                                      year: "numeric",
                                                    })}
                                                    {isPlaid && <Badge variant="secondary">Plaid</Badge>}
                                                  </div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                  {stmt.startingValue != null ? `$${stmt.startingValue.toLocaleString()}` : "-"}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                  {stmt.endingValue != null ? `$${stmt.endingValue.toLocaleString()}` : "-"}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                  {stmt.deposits != null ? `$${stmt.deposits.toLocaleString()}` : "-"}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                  {stmt.withdrawals != null ? `$${stmt.withdrawals.toLocaleString()}` : "-"}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                  {twr != null ? (
                                                    <span className={twr >= 0 ? "text-green-600" : "text-red-600"}>
                                                      {twr >= 0 ? "+" : ""}
                                                      {twr.toFixed(2)}%
                                                    </span>
                                                  ) : (
                                                    "-"
                                                  )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                  <div className="flex justify-end gap-1">
                                                    {isPlaid ? (
                                                      <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDownloadPlaidStatement(stmt)}
                                                      >
                                                        Download PDF
                                                      </Button>
                                                    ) : (
                                                      <>
                                                        <Button variant="ghost" size="sm" onClick={() => setEditingStatement({ ...stmt })}>
                                                          Edit
                                                        </Button>
                                                        <Button
                                                          variant="ghost"
                                                          size="sm"
                                                          className="text-red-600 hover:text-red-700"
                                                          onClick={() => setDeletingStatementId(stmt.id)}
                                                        >
                                                          Delete
                                                        </Button>
                                                      </>
                                                    )}
                                                  </div>
                                                </TableCell>
                                              </TableRow>
                                            );
                                          })}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              </CollapsibleContent>
                              </>
                            </Collapsible>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Statement Dialog */}
      <Dialog
        open={!!editingStatement}
        onOpenChange={(open) => { if (!open) setEditingStatement(null); }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Account Statement</DialogTitle>
            <DialogDescription>Update account statement details</DialogDescription>
          </DialogHeader>
          {editingStatement && (
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Statement Date</Label>
                <Input
                  type="date"
                  value={editingStatement.statementDate}
                  onChange={(e) =>
                    setEditingStatement({ ...editingStatement, statementDate: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={editingStatement.startDate}
                  onChange={(e) =>
                    setEditingStatement({ ...editingStatement, startDate: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={editingStatement.endDate}
                  onChange={(e) =>
                    setEditingStatement({ ...editingStatement, endDate: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Period Type</Label>
                <Select
                  value={editingStatement.periodType}
                  onValueChange={(value) =>
                    setEditingStatement({ ...editingStatement, periodType: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DAILY">Daily</SelectItem>
                    <SelectItem value="MONTHLY">Monthly</SelectItem>
                    <SelectItem value="YEARLY">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Starting Value</Label>
                <Input
                  type="number"
                  value={editingStatement.startingValue}
                  onChange={(e) =>
                    setEditingStatement({ ...editingStatement, startingValue: parseFloat(e.target.value) || 0 })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Ending Value</Label>
                <Input
                  type="number"
                  value={editingStatement.endingValue}
                  onChange={(e) =>
                    setEditingStatement({ ...editingStatement, endingValue: parseFloat(e.target.value) || 0 })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Deposits</Label>
                <Input
                  type="number"
                  value={editingStatement.deposits}
                  onChange={(e) =>
                    setEditingStatement({ ...editingStatement, deposits: parseFloat(e.target.value) || 0 })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Withdrawals</Label>
                <Input
                  type="number"
                  value={editingStatement.withdrawals}
                  onChange={(e) =>
                    setEditingStatement({ ...editingStatement, withdrawals: parseFloat(e.target.value) || 0 })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Fees</Label>
                <Input
                  type="number"
                  value={editingStatement.fees}
                  onChange={(e) =>
                    setEditingStatement({ ...editingStatement, fees: parseFloat(e.target.value) || 0 })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Dividends Earned</Label>
                <Input
                  type="number"
                  value={editingStatement.dividendsEarned}
                  onChange={(e) =>
                    setEditingStatement({ ...editingStatement, dividendsEarned: parseFloat(e.target.value) || 0 })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Interest Earned</Label>
                <Input
                  type="number"
                  value={editingStatement.interestEarned}
                  onChange={(e) =>
                    setEditingStatement({ ...editingStatement, interestEarned: parseFloat(e.target.value) || 0 })
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingStatement(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEditedStatement}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Statement Confirmation Dialog */}
      <Dialog
        open={!!deletingStatementId}
        onOpenChange={(open) => { if (!open) setDeletingStatementId(null); }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Statement</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this statement? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingStatementId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingStatementId && handleDeleteStatement(deletingStatementId)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog open={!!editingAccount} onOpenChange={(open) => { if (!open) { setEditingAccount(null); setNewPosition({ symbol: "", name: "", shares: "", avgCost: "", sector: "" }); setDeletedPositionIndices([]); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Account</DialogTitle>
            <DialogDescription>Update account details and positions</DialogDescription>
          </DialogHeader>
          {editingAccount && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Account Name</Label>
                  <Input
                    value={editingAccount.accountName}
                    onChange={(e) => setEditingAccount({ ...editingAccount, accountName: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Account Number</Label>
                  <Input
                    value={editingAccount.accountNumber}
                    onChange={(e) => setEditingAccount({ ...editingAccount, accountNumber: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Custodian</Label>
                  <Input
                    value={editingAccount.custodian}
                    onChange={(e) => setEditingAccount({ ...editingAccount, custodian: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Custodian Account #</Label>
                  <Input
                    value={editingAccount.accountNumberCustodian}
                    onChange={(e) => setEditingAccount({ ...editingAccount, accountNumberCustodian: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Account Type</Label>
                  <Select
                    value={editingAccount.accountType}
                    onValueChange={(value) => setEditingAccount({ ...editingAccount, accountType: value as AccountTypeKey })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ACCOUNT_TYPES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Ownership Type</Label>
                  <Select
                    value={editingAccount.ownershipType}
                    onValueChange={(value) => setEditingAccount({ ...editingAccount, ownershipType: value as keyof typeof OWNERSHIP_TYPES })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(OWNERSHIP_TYPES).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Balance</Label>
                  <Input
                    type="number"
                    value={editingAccount.balance}
                    onChange={(e) => setEditingAccount({ ...editingAccount, balance: Number(e.target.value) })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Cash Balance</Label>
                  <Input
                    type="number"
                    value={editingAccount.cashBalance}
                    onChange={(e) => setEditingAccount({ ...editingAccount, cashBalance: Number(e.target.value) })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>YTD Return (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editingAccount.ytdReturn}
                    onChange={(e) => setEditingAccount({ ...editingAccount, ytdReturn: Number(e.target.value) })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Status</Label>
                  <Select
                    value={editingAccount.status}
                    onValueChange={(value) => setEditingAccount({ ...editingAccount, status: value as "Active" | "Pending" | "Closed" })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Positions */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Positions</h3>

                {/* Add Position Form */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Symbol *</Label>
                    <Input
                      placeholder="AAPL"
                      value={newPosition.symbol}
                      onChange={(e) => setNewPosition({ ...newPosition, symbol: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      placeholder="Apple Inc"
                      value={newPosition.name}
                      onChange={(e) => setNewPosition({ ...newPosition, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sector</Label>
                    <Input
                      placeholder="Technology"
                      value={newPosition.sector}
                      onChange={(e) => setNewPosition({ ...newPosition, sector: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Shares *</Label>
                    <Input
                      type="number"
                      placeholder="100"
                      value={newPosition.shares}
                      onChange={(e) => setNewPosition({ ...newPosition, shares: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Avg Cost (per share) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="150.00"
                      value={newPosition.avgCost}
                      onChange={(e) => setNewPosition({ ...newPosition, avgCost: e.target.value })}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleAddPositionToEdit}
                      disabled={!newPosition.symbol || !newPosition.shares || !newPosition.avgCost}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Position
                    </Button>
                  </div>
                </div>

                {/* Positions List */}
                {(editingAccount.positions || []).filter((p) => !p.delete).length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-muted-foreground">
                      Positions ({(editingAccount.positions || []).filter((p) => !p.delete).length})
                    </h4>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted sticky top-0">
                            <tr>
                              <th className="text-left p-2">Symbol</th>
                              <th className="text-left p-2">Name</th>
                              <th className="text-left p-2">Sector</th>
                              <th className="text-right p-2">Shares</th>
                              <th className="text-right p-2">Avg Cost</th>
                              <th className="text-right p-2">Total</th>
                              <th className="w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(editingAccount.positions || []).map((pos, idx) =>
                              pos.delete ? null : (
                                <tr key={idx} className="border-t">
                                  <td className="p-2">{pos.symbol}</td>
                                  <td className="p-2 text-muted-foreground">{pos.name}</td>
                                  <td className="p-2">
                                    <Badge variant="outline" className="text-xs">
                                      {isCashEquivalentPosition(pos) ? "Cash & Equivalents" : pos.sector}
                                    </Badge>
                                  </td>
                                  <td className="p-2 text-right">{pos.shares}</td>
                                  <td className="p-2 text-right">${pos.avgCost.toFixed(2)}</td>
                                  <td className="p-2 text-right">${(pos.shares * pos.avgCost).toFixed(2)}</td>
                                  <td className="p-2">
                                    <button
                                      onClick={() => handleRemovePositionFromEdit(idx)}
                                      className="text-muted-foreground hover:text-destructive"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingAccount(null); setNewPosition({ symbol: "", name: "", shares: "", avgCost: "", sector: "" }); setDeletedPositionIndices([]); }}>Cancel</Button>
            <Button onClick={() => editingAccount && handleEditAccount(editingAccount)} disabled={isSavingAccount}>
              {isSavingAccount && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Position Dialog */}
      <Dialog open={!!editingPosition} onOpenChange={(open) => { if (!open) { setEditingPosition(null); setEditingPositionAccountId(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Position</DialogTitle>
            <DialogDescription>Update position details</DialogDescription>
          </DialogHeader>
          {editingPosition && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Symbol</Label>
                  <Input
                    value={editingPosition.symbol}
                    disabled
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Name</Label>
                  <Input
                    value={editingPosition.name}
                    disabled
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Shares</Label>
                  <Input
                    type="number"
                    value={editingPosition.shares}
                    onChange={(e) => setEditingPosition({ ...editingPosition, shares: Number(e.target.value) })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Avg Cost</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingPosition.avgCost}
                    onChange={(e) => setEditingPosition({ ...editingPosition, avgCost: Number(e.target.value) })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Current Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingPosition.price}
                    disabled
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Sector</Label>
                  <Input
                    value={editingPosition.sector}
                    disabled
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingPosition(null); setEditingPositionAccountId(null); }}>Cancel</Button>
            <Button onClick={() => editingPosition && editingPositionAccountId && handleEditPosition(editingPositionAccountId, editingPosition)}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Position Confirmation Dialog */}
      <Dialog open={!!deletingPosition} onOpenChange={(open) => { if (!open) { setDeletingPosition(null); setDeletingPositionAccountId(null); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Position</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {deletingPosition?.symbol}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeletingPosition(null); setDeletingPositionAccountId(null); }}>Cancel</Button>
            <Button variant="destructive" onClick={() => deletingPosition && deletingPositionAccountId && handleDeletePosition(deletingPositionAccountId, deletingPosition)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddStatement
        open={isAddingStatement}
        onClose={() => {
          setIsAddingStatement(false);
          fetchStatements();
        }}
        accounts={accounts}
        userId={userId}
      />

      <InviteOthers
        open={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        sender={localStorage.getItem('userEmail') || ""}
      />

      <ScheduleMeetingDialog
        professional={selectedProfessional}
        isOpen={isScheduleMeetingOpen}
        onOpenChange={setIsScheduleMeetingOpen}
        onScheduleSuccess={() => { fetchMeetings(); }}
      />
    </div>
  );
}
